import { create } from 'zustand'
import { api } from '../api/client'
import type { AppNotification } from '../types'

interface NotificationState {
  notifications: AppNotification[]
  unreadCount: number
  loading: boolean
  panelOpen: boolean

  fetchNotifications: () => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  deleteNotification: (id: string) => Promise<void>
  togglePanel: () => void
  closePanel: () => void
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  panelOpen: false,

  fetchNotifications: async () => {
    set({ loading: true })
    try {
      const data = await api.get<{ notifications: AppNotification[]; unread_count: number }>(
        '/api/notifications?limit=50'
      )
      set({ notifications: data.notifications, unreadCount: data.unread_count, loading: false })
    } catch {
      set({ loading: false })
    }
  },

  fetchUnreadCount: async () => {
    try {
      const data = await api.get<{ unread_count: number }>('/api/notifications/unread-count')
      set({ unreadCount: data.unread_count })
    } catch {
      // silent
    }
  },

  markRead: async (id: string) => {
    try {
      await api.post(`/api/notifications/${id}/read`)
      set((s) => ({
        notifications: s.notifications.map((n) =>
          n.id === id ? { ...n, status: 'read' as const } : n
        ),
        unreadCount: Math.max(0, s.unreadCount - 1),
      }))
    } catch {
      // silent
    }
  },

  markAllRead: async () => {
    try {
      await api.post('/api/notifications/read-all')
      set((s) => ({
        notifications: s.notifications.map((n) => ({ ...n, status: 'read' as const })),
        unreadCount: 0,
      }))
    } catch {
      // silent
    }
  },

  deleteNotification: async (id: string) => {
    try {
      await api.delete(`/api/notifications/${id}`)
      set((s) => ({
        notifications: s.notifications.filter((n) => n.id !== id),
        unreadCount: s.notifications.find((n) => n.id === id)?.status === 'unread'
          ? Math.max(0, s.unreadCount - 1)
          : s.unreadCount,
      }))
    } catch {
      // silent
    }
  },

  togglePanel: () => {
    const wasOpen = get().panelOpen
    set({ panelOpen: !wasOpen })
    if (!wasOpen) {
      get().fetchNotifications()
    }
  },

  closePanel: () => set({ panelOpen: false }),
}))
